import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { apiFetch } from '@/lib/api'
import { useCatalogueContext } from '@/lib/catalogue-context'

interface TrainingOffer {
  id: string
  title: string
  description: string
  topic: string
  durationH: number
  price: number
  maxPax: number | null
  isRemote: boolean
  city: string | null
  status: string
  createdAt: string
  provider: { id: string; firstName: string; lastName: string; rating: number; ratingCount: number; avatarUrl: string | null } | null
  company: { id: string; name: string; logoUrl: string | null } | null
  dates: { id: string; date: string }[]
  _count: { registrations: number }
}

const TOPICS = ['Sécurité incendie', 'Premiers secours', 'Habilitation électrique', 'Évacuation', 'Formation SST', 'Autre']

export default function CataloguePage() {
  const { hasNav } = useCatalogueContext()
  const [offers, setOffers] = useState<TrainingOffer[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState({ topic: '', city: '', remote: '' })

  const load = useCallback(async () => {
    setLoading(true)
    const params = new URLSearchParams()
    if (search.topic) params.set('topic', search.topic)
    if (search.city) params.set('city', search.city)
    if (search.remote === 'true' || search.remote === 'false') params.set('remote', search.remote)
    const data = await apiFetch<TrainingOffer[]>(`/catalogue?${params}`)
    setOffers(Array.isArray(data) ? data : [])
    setLoading(false)
  }, [search])

  useEffect(() => { load() }, [load])

  function fmt(cents: number) {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100)
  }

  const nextDate = (dates: { date: string }[]) => {
    const future = dates.filter((d) => new Date(d.date) >= new Date()).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    return future[0] ? new Date(future[0].date).toLocaleDateString('fr-FR') : null
  }

  return (
    <div className="min-h-screen" style={hasNav ? undefined : { background: 'linear-gradient(135deg, #0A0A14 0%, #0D0D2B 50%, #0A0A14 100%)' }}>
      {/* Header public (masqué quand sidebar présente) */}
      {!hasNav && (
        <header className="border-b border-white/[0.08] px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-text-primary font-bold text-xl tracking-tight">Velink</Link>
          <div className="flex gap-3">
            <Link to="/login" className="glass-btn px-4 py-2 text-sm rounded-xl">Connexion</Link>
          </div>
        </header>
      )}

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-text-primary mb-3">Catalogue des formations</h1>
          <p className="text-text-secondary text-lg">Trouvez la formation adaptée à vos besoins parmi nos prestataires certifiés.</p>
        </div>

        {/* Filtres */}
        <div className="glass-card p-5 mb-8 flex flex-wrap gap-4">
          <div className="flex-1 min-w-[180px]">
            <label className="text-text-muted text-xs font-medium block mb-1.5">Thème</label>
            <select
              value={search.topic}
              onChange={(e) => setSearch((v) => ({ ...v, topic: e.target.value }))}
              className="glass-input w-full text-sm"
            >
              <option value="">Tous les thèmes</option>
              {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="text-text-muted text-xs font-medium block mb-1.5">Ville</label>
            <input
              type="text"
              value={search.city}
              onChange={(e) => setSearch((v) => ({ ...v, city: e.target.value }))}
              placeholder="ex. Lyon"
              className="glass-input w-full text-sm"
            />
          </div>
          <div className="min-w-[160px]">
            <label className="text-text-muted text-xs font-medium block mb-1.5">Modalité</label>
            <select
              value={search.remote}
              onChange={(e) => setSearch((v) => ({ ...v, remote: e.target.value }))}
              className="glass-input w-full text-sm"
            >
              <option value="">Toutes</option>
              <option value="false">Présentiel</option>
              <option value="true">Distanciel</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => <div key={i} className="glass-card h-56 animate-pulse" />)}
          </div>
        ) : offers.length === 0 ? (
          <div className="glass-card p-16 text-center">
            <p className="text-5xl mb-4">🎓</p>
            <p className="text-text-primary font-semibold mb-2">Aucune formation trouvée</p>
            <p className="text-text-secondary text-sm">Essayez d'autres critères de recherche.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {offers.map((offer) => {
              const nd = nextDate(offer.dates)
              const spotsLeft = offer.maxPax ? offer.maxPax - offer._count.registrations : null
              return (
                <Link key={offer.id} to={`/catalogue/${offer.id}`} className="block glass-card p-5 hover:border-white/20 transition-all group">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-accent/10 border border-accent/20 text-accent mb-2 inline-block">{offer.topic}</span>
                      <h3 className="text-text-primary font-semibold group-hover:text-accent transition-colors">{offer.title}</h3>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-accent font-bold text-lg">{fmt(offer.price)}</p>
                      <p className="text-text-muted text-[10px]">/personne</p>
                    </div>
                  </div>
                  <p className="text-text-secondary text-sm line-clamp-2 mb-3">{offer.description}</p>
                  <div className="flex items-center gap-3 text-xs text-text-muted flex-wrap">
                    <span>⏱ {offer.durationH}h</span>
                    {offer.isRemote ? <span>🖥 Distanciel</span> : <span>📍 {offer.city}</span>}
                    {nd && <span>📅 Prochain : {nd}</span>}
                    {spotsLeft !== null && <span>👥 {spotsLeft} place{spotsLeft !== 1 ? 's' : ''} restante{spotsLeft !== 1 ? 's' : ''}</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/[0.06]">
                    <div className="w-7 h-7 rounded-full bg-accent/15 flex items-center justify-center">
                      <span className="text-accent text-xs font-bold">
                        {offer.provider ? offer.provider.firstName[0] : (offer.company?.name[0] ?? '?')}
                      </span>
                    </div>
                    <div>
                      <p className="text-text-primary text-xs font-medium">
                        {offer.provider ? `${offer.provider.firstName} ${offer.provider.lastName}` : (offer.company?.name ?? 'Centre de formation')}
                      </p>
                      {offer.provider && (
                        <p className="text-text-muted text-[10px]">
                          {offer.provider.ratingCount > 0 ? `⭐ ${offer.provider.rating.toFixed(1)}` : 'Pas encore d\'avis'}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
